import { createElement, getUiRoot, setText } from './dom';
import { setLabel } from './i18n';
import { createIcon } from './icons';
import { createHistoricalSource } from './historicalSource';
import type { TourRoute, TourSnapshot } from '../tours/types';
import type { TourActions } from './tourControls';

function storyButton(parent: HTMLElement, label: string): HTMLButtonElement {
  const button = createElement('button', 'tour-button', parent);
  button.type = 'button';
  setText(button, label);
  return button;
}

export function stopTourControlKey(event: KeyboardEvent): void {
  if (/^(Space|Enter|Arrow.*|Home|End|PageUp|PageDown)$/.test(event.code)) event.stopPropagation();
}

function storyLayout() {
  const root = createElement('section', 'tour-story', getUiRoot());
  root.addEventListener('keydown', stopTourControlKey);
  root.addEventListener('keyup', stopTourControlKey);
  setLabel(root, 'aria-label', 'Tour commentary');
  root.hidden = true;
  const header = createElement('div', 'tour-story-header', root);
  const title = createElement('h2', 'tour-story-title', header);
  const close = createElement('button', 'tour-button tour-story-close', header);
  close.type = 'button';
  setLabel(close, 'aria-label', 'Take over');
  setLabel(close, 'title', 'Take over');
  const icon = createIcon('close', 16);
  icon.setAttribute('aria-hidden', 'true');
  close.append(icon);
  const text = createElement('p', 'tour-story-text', root);
  const footer = createElement('div', 'tour-story-footer', root);
  const previous = storyButton(footer, 'Previous stop');
  const pause = storyButton(footer, 'Pause tour');
  const next = storyButton(footer, 'Next stop');
  return { root, title, text, close, previous, pause, next };
}

export function createTourStory(routes: readonly TourRoute[], actions: TourActions) {
  const { root, title, text, close, previous, pause, next } = storyLayout();
  close.addEventListener('click', actions.stop);
  let current: TourSnapshot | null = null;
  const source = createHistoricalSource(root, () => {
    if (current && !current.paused) actions.pause();
  });
  const chapterIndex = () => {
    const route = routes.find((route) => route.id === current?.id);
    const chapter = current?.chapter;
    return { route, index: chapter ? (route?.chapters?.indexOf(chapter) ?? -1) : -1 };
  };
  const jump = (delta: number): void => {
    const { route, index } = chapterIndex();
    const chapter = route?.chapters?.[index + delta];
    if (route && chapter) actions.seek(chapter.at * route.duration + 0.001);
  };
  previous.addEventListener('click', () => {
    jump(-1);
  });
  next.addEventListener('click', () => {
    jump(1);
  });
  pause.addEventListener('click', actions.pause);
  return {
    update(state: TourSnapshot | null): void {
      current = state;
      root.hidden = !state?.chapter;
      source.set(state?.chapter?.source ?? null);
      if (!state?.chapter) return;
      setText(title, state.chapter.title);
      setText(text, state.chapter.text);
      setText(pause, state.paused ? 'Resume tour' : 'Pause tour');
      const { route, index } = chapterIndex();
      previous.disabled = index <= 0;
      next.disabled = index < 0 || index >= (route?.chapters?.length ?? 0) - 1;
    },
    setVisible(visible: boolean): void {
      root.hidden = !visible || !current?.chapter;
      if (!visible) source.close();
    },
    dispose(): void {
      root.remove();
    },
  };
}
